import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { PricingBand } from "@/components/marketing/PricingBand";
import { CoachProof } from "@/components/marketing/CoachProof";
import { PublicShell } from "@/components/marketing/PublicShell";
import { WorkoutLogPreview } from "@/components/marketing/WorkoutLogPreview";
import { useAuth } from "@/components/auth/AuthProvider";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { SectionErrorBoundary } from "@/components/ui/SectionErrorBoundary";
import { ROUTES } from "@/constants/routes";
import { usePublicAnalytics } from "@/hooks/usePublicAnalytics";
import { capturePendingReferral } from "@/hooks/useReferral";

const ProgressProof = lazy(
  () => import("@/components/marketing/ProgressProof"),
);
const heroImagePriority = { fetchpriority: "high" } as const;

interface ProgressProofPlaceholderProps {
  loadingLabel: string;
  targetRef?: RefObject<HTMLElement>;
}

function ProgressProofPlaceholder({
  loadingLabel,
  targetRef,
}: ProgressProofPlaceholderProps) {
  return (
    <section
      ref={targetRef}
      id="progress-proof"
      className="flex min-h-[520px] items-center justify-center bg-muted-bg px-4"
    >
      <div role="status" aria-live="polite">
        <LoadingSpinner size="lg" text={loadingLabel} />
      </div>
    </section>
  );
}

function DeferredProgressProof() {
  const { t } = useTranslation();
  const placeholderRef = useRef<HTMLElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const loadingLabel = t("common.loading");

  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return;
    }

    const target = placeholderRef.current;
    if (!target) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        setShouldLoad(true);
      },
      { rootMargin: "600px 0px" },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  if (!shouldLoad) {
    return (
      <ProgressProofPlaceholder
        loadingLabel={loadingLabel}
        targetRef={placeholderRef}
      />
    );
  }

  return (
    <SectionErrorBoundary sectionName={t("frontpage.precision.nav.progress")}>
      <Suspense
        fallback={<ProgressProofPlaceholder loadingLabel={loadingLabel} />}
      >
        <ProgressProof />
      </Suspense>
    </SectionErrorBoundary>
  );
}

export default function Frontpage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { track } = usePublicAnalytics();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) capturePendingReferral(ref);
  }, []);

  useEffect(() => {
    document.title = `${t("frontpage.precision.hero.brand")} | ${t("frontpage.precision.hero.headline")}`;
  }, [t]);

  if (user) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <PublicShell>
      <section
        id="public-hero"
        className="relative min-h-[720px] overflow-hidden bg-[#101211] text-white"
      >
        <picture aria-hidden="true">
          <source
            media="(max-width: 767px)"
            srcSet="/media/precision-hero-mobile.jpg"
          />
          <img
            {...heroImagePriority}
            src="/media/precision-hero-desktop.jpg"
            alt=""
            width={1920}
            height={1200}
            className="absolute inset-0 h-full w-full object-cover object-center md:object-[62%_50%]"
          />
        </picture>
        <div aria-hidden="true" className="absolute inset-0 bg-black/45" />

        <div className="relative z-10 mx-auto flex min-h-[720px] max-w-[1280px] items-start px-4 pb-16 pt-32 md:items-center md:px-8 md:py-20">
          <div className="max-w-[640px]">
            <h1 className="font-display text-[44px] font-bold leading-none text-white md:text-[72px]">
              {t("frontpage.precision.hero.brand")}
            </h1>
            <p className="mt-3 max-w-[12ch] font-display text-[36px] font-bold leading-[1.05] text-[hsl(var(--brand-on-dark))] md:text-[56px]">
              {t("frontpage.precision.hero.headline")}
            </p>
            <p className="mt-5 max-w-[44ch] text-base leading-7 text-white/85 md:text-lg">
              {t("frontpage.precision.hero.body")}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to={ROUTES.SIGNUP}
                onClick={() => void track("public_cta_clicked", "hero_primary")}
                className="inline-flex min-h-12 items-center justify-center gap-2 bg-brand px-6 text-base font-semibold text-primary-foreground transition-colors duration-150 hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#101211]"
              >
                {t("frontpage.precision.hero.primary")}
                <ArrowRight className="h-5 w-5" aria-hidden />
              </Link>
              <a
                href="#product-proof"
                onClick={() => void track("public_nav_clicked", "hero_product_proof")}
                className="inline-flex min-h-12 items-center justify-center border border-white/75 bg-black/20 px-6 text-base font-semibold text-white transition-colors duration-150 hover:bg-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#101211]"
              >
                {t("frontpage.precision.hero.secondary")}
              </a>
            </div>
            <p className="mt-4 text-sm leading-6 text-white/75">
              {t("frontpage.precision.hero.trial")}
            </p>
          </div>
        </div>
      </section>

      <WorkoutLogPreview />
      <DeferredProgressProof />
      <CoachProof />
      <PricingBand />
    </PublicShell>
  );
}
